import { ReactNode, useCallback, useMemo, useState } from 'react';
import { SearchInput } from '../SearchInput';
import SweepSelect, { SweepSelectProps } from '../SweepSelect';
import { Typography, colors } from '@sweep-io/sweep-design';
import StyledTooltip from '../StyledTooltip';
import keyBy from 'lodash/keyBy';
import { highlightMatch } from '../../../lib/highlightMatch';
import { Box, ListSubheader, MenuItem, SelectProps, styled } from '@mui/material';

export interface SearchSelectItem<T = any> {
  value: string;
  label: string; // Renders in the list. This is also used for the search
  tooltip?: ReactNode;
  data?: T;
  isSection?: boolean;
  disabled?: boolean;
}

interface SearchSelectProps<T = any> extends Omit<SweepSelectProps, 'children' | 'renderValue'> {
  items: SearchSelectItem<T>[];
  valueRenderer?: (item: SearchSelectItem<T>) => React.ReactNode;
  itemRenderer?: (item: SearchSelectItem<T>, search?: string) => React.ReactNode;
  onChange?: (item: SearchSelectItem<T>) => void;
  searchPlaceholder?: string;
  children?: ReactNode;
  emptyResultsMessage?: string;
}

const defaultItemRenderer = (item: SearchSelectItem, search?: string) => (
  <>{highlightMatch(item.label, search)}</>
);

export const SearchSelectListSubheader = styled(ListSubheader)(() => ({
  display: 'flex',
  alignItems: 'center',
  lineHeight: 1,
  borderColor: colors.grey[300],
  margin: '0px -8px',
  padding: '14px 15px 15px',
  position: 'sticky',
  gap: '12px',
}));

const DEFAULT_EMPTY_RESULTS_MESSAGE = 'No results found';

export function SearchSelect<T = any>({
  items,
  valueRenderer,
  onChange,
  searchPlaceholder,
  children,
  itemRenderer = defaultItemRenderer,
  emptyResultsMessage = DEFAULT_EMPTY_RESULTS_MESSAGE,
  ...sweepSelectProps
}: SearchSelectProps<T>) {
  const [search, setSearch] = useState('');
  const [localValue, setLocalValue] = useState(
    sweepSelectProps?.SelectProps?.value || sweepSelectProps?.SelectProps?.defaultValue,
  );

  const itemsByKey = useMemo(() => keyBy(items, 'value'), [items]);

  const localItems = useMemo(() => {
    return items.filter((item) => {
      if (item.isSection) {
        return false;
      }
      return localValue === item.value || item.label.toUpperCase().includes(search.toUpperCase());
    });
  }, [items, localValue, search]);

  const selectProps: SelectProps = useMemo(() => {
    return {
      ...sweepSelectProps.SelectProps,
      onChange: (event) => {
        setLocalValue(event.target.value as string);
        const item = itemsByKey[event.target.value as string];
        item && onChange && onChange(item);
      },
      inputProps: {
        ...sweepSelectProps.SelectProps?.inputProps,
        onChange: (event) => {
          setLocalValue((event.target as any).value);
          sweepSelectProps.SelectProps?.inputProps?.onChange &&
            sweepSelectProps.SelectProps?.inputProps?.onChange(event);
        },
      },
    };
  }, [itemsByKey, onChange, sweepSelectProps.SelectProps]);

  const renderValue = useCallback(
    (value: unknown) => {
      const item = items.find((item) => item.value === value);
      if (valueRenderer) {
        return valueRenderer(item as SearchSelectItem<T>);
      }
      return <>{item?.label || (value as any)}</>;
    },
    [items, valueRenderer],
  );

  const maybeRenderEmptyResults = () => {
    if (emptyResultsMessage && localItems.length === 0) {
      return (
        <Box pl={1} pb={1}>
          <Typography variant="caption">{emptyResultsMessage}</Typography>
        </Box>
      );
    }
  };

  return (
    <SweepSelect
      {...sweepSelectProps}
      MenuProps={{
        ...sweepSelectProps.MenuProps,
        autoFocus: false,
        sx: {
          '.MuiList-root': {
            paddingTop: 0,
          },
        },
      }}
      SelectProps={selectProps}
      renderValue={renderValue}
      onOpenCb={(event) => {
        setSearch('');
        sweepSelectProps.onOpenCb && sweepSelectProps.onOpenCb(event);
      }}
    >
      {!Boolean(sweepSelectProps.isLoading) && !children && (
        <SearchSelectListSubheader>
          <SearchInput
            withFixedMagnifyingGlassIcon
            placeholder={searchPlaceholder}
            TextFieldProps={{
              sx: { minWidth: '270px' },
              value: search,
              autoFocus: true,
              onChange: (event) => {
                const value = event.target.value;
                setSearch(value);
              },
              onKeyDown: (e) => {
                if (e.key !== 'Escape') {
                  // Prevents autoselecting item while typing (default Select behaviour)
                  e.stopPropagation();
                }
              },
              onClick: (e) => {
                e.stopPropagation();
              },
            }}
          />
        </SearchSelectListSubheader>
      )}
      {maybeRenderEmptyResults()}
      {!children && <MenuItem value="" hidden />}
      {!children &&
        (localItems || items).map((item) =>
          item.isSection ? (
            <MenuItem key={item.value} value={item.value} disabled>
              <StyledTooltip title={item.tooltip} key={item.value} placement="right">
                <Typography variant="caption-bold">{item.label}</Typography>
              </StyledTooltip>
            </MenuItem>
          ) : (
            <MenuItem
              value={item.value}
              key={item.value}
              autoFocus={item.value === localValue}
              disabled={item.disabled}
            >
              <StyledTooltip
                title={item.tooltip}
                PopperProps={{
                  sx: {
                    zIndex: 999999, // Needs to be higher than the dropdown of the select
                  },
                }}
                placement="right"
              >
                <Box>{itemRenderer ? itemRenderer(item, search) : item.label}</Box>
              </StyledTooltip>
            </MenuItem>
          ),
        )}
      {children}
    </SweepSelect>
  );
}
